import { inject, Injectable } from '@angular/core';
import { ConfigStateService, LocalizationService } from '@abp/ng.core';
import {
  flatArrayDeepToObject,
  TranslateService,
} from '@volo/ngx-lepton-x.core';
import { LPX_TRANSLATE_KEY_MAP_TOKEN } from '@volosoft/abp.ng.theme.lepton-x';
import { map, Observable, of } from 'rxjs';

@Injectable()
export class LpxCustomTranslateService implements TranslateService {
  private readonly localization = inject(LocalizationService);
  private readonly configState = inject(ConfigStateService);
  private readonly translateKeys = inject<Array<Record<string, string>>>(
    LPX_TRANSLATE_KEY_MAP_TOKEN,
  );

  get(key: string, defaultValue?: string): string {
    const customTranslation = this.getCustomTranslation(key);
    if (customTranslation) {
      return customTranslation;
    }

    const keyToTranslate = this.getMappedKey(key);
    if (keyToTranslate) {
      return this.localization.instant({
        key: keyToTranslate,
        defaultValue,
      });
    }

    return defaultValue || key;
  }

  get$(key: string, defaultValue?: string): Observable<string> {
    const customTranslation = this.getCustomTranslation(key);
    if (customTranslation) {
      return this.configState
        .getDeep$('localization.currentCulture.cultureName')
        .pipe(map(() => this.getCustomTranslation(key) || defaultValue || key));
    }

    const keyToTranslate = this.getMappedKey(key);
    if (keyToTranslate) {
      return this.localization.get({
        key: keyToTranslate,
        defaultValue,
      });
    }

    return of(defaultValue || key);
  }

  private getMappedKey(key: string): string | undefined {
    const keys = flatArrayDeepToObject(this.translateKeys);
    
    if (keys[key]) {
      return key.includes('::') ? key : undefined;
    }

    return undefined
  }

  private getCustomTranslation(key: string): string | undefined {
    const culture =
      this.configState.getDeep('localization.currentCulture.cultureName') || '';
    const isSpanish = culture.toLowerCase().startsWith('es');

    if (!isSpanish) {
      return undefined;
    }

    return (
      {
        'language.settings.title': 'Opciones de idioma',
        'theme.settings.title': 'Apariencia',
        'theme.settings.darkMode': 'Oscuro',
        'theme.settings.lightMode': 'Claro',
        'theme.settings.semiDarkMode': 'Semioscuro',
        'theme.settings.system': 'Sistema',
        'settings.title': 'Ancho del contenedor',
        'settings.boxed': 'En caja',
        'settings.fullWidth': 'Ancho completo',
        'settings.fluid': 'Fluido',
        'generalSettings.title': 'Configuracion general',
        'menuFilter.text': 'Filtrar menu',
        'mobileMenu.settings.title': 'Configuracion',
        'mobileMenu.menu.title': 'Menu',
        'toolbar.welcome': 'Bienvenido',
      } as Record<string, string>
    )[key];
  }
}
